// Helper functions for Guide/Lineup Editor
import type { ChannelLineupType } from '@/stores/api/hub-content-api'

import { v4 as uuidv4 } from 'uuid'

import type {
  Channel,
  ChannelGroup,
  GroupOverlap,
  OverlapSuggestion,
  ValidationError,
  ValidationResult
} from './guideTypes'

/**
 * Generate a unique ID for frontend use
 */
export function generateId(): string {
  return uuidv4()
}

/**
 * Flatten groups to backend format (flat array of channels)
 * Channels are collected in order and sorted by logicalChannel
 */
function flattenGroupsToLineup(groups: ChannelGroup[]): Channel[] {
  const allChannels: Channel[] = []

  for (const group of groups) {
    for (const channel of group.channels) {
      allChannels.push(channel)
    }
  }

  // Sort by logicalChannel
  return allChannels.sort(
    (a, b) => (a.logicalChannel ?? 0) - (b.logicalChannel ?? 0)
  )
}

/**
 * Normalize category for comparison
 * Treats empty/undefined categories as a distinct '__uncategorized__' category
 */
function normalizeCategory(category: string | undefined): string {
  if (!category || category.trim() === '') {
    return '__uncategorized__'
  }
  return category.trim().toLowerCase()
}

/**
 * Check if any channels have category info
 */
function hasAnyCategoryInfo(channels: Channel[]): boolean {
  return channels.some((ch) => ch.category && ch.category.trim() !== '')
}

/**
 * Unflatten backend lineup to frontend groups
 * Groups are detected based on:
 * - Category changes (if channels have category info) - same category channels merge even across gaps
 * - Gaps in channel numbering (if no category info) - gap > 1 starts new group
 */
function unflattenLineupToGroups(channels: Channel[]): ChannelGroup[] {
  if (channels.length === 0) {
    return []
  }

  // Sort channels by logicalChannel
  const sorted = [...channels].sort(
    (a, b) => (a.logicalChannel ?? 0) - (b.logicalChannel ?? 0)
  )

  // Choose strategy based on whether channels have category info
  if (hasAnyCategoryInfo(sorted)) {
    return unflattenWithCategoryAwareness(sorted)
  }

  return unflattenWithGapDetection(sorted)
}

/**
 * Group channels by category changes
 * Same category channels stay together even across gaps
 * Category changes always start a new group
 */
function unflattenWithCategoryAwareness(sorted: Channel[]): ChannelGroup[] {
  const groups: ChannelGroup[] = []
  let currentGroup: Channel[] = []
  let currentStartNumber: number | null = sorted[0]?.logicalChannel ?? null
  let currentCategory: string | undefined = sorted[0]?.category

  for (let i = 0; i < sorted.length; i++) {
    const channel = sorted[i]
    const prevChannel = sorted[i - 1]

    if (prevChannel) {
      // Normalize categories for comparison
      const prevCat = normalizeCategory(prevChannel.category)
      const currCat = normalizeCategory(channel.category)

      // Check for category change
      if (prevCat !== currCat) {
        // Category changed - finalize current group
        const endNumber =
          currentGroup.length > 0 &&
          currentGroup[currentGroup.length - 1].logicalChannel != null
            ? currentGroup[currentGroup.length - 1].logicalChannel
            : null
        groups.push({
          id: generateId(),
          startNumber: currentStartNumber,
          endNumber,
          channels: currentGroup,
          category: currentCategory
        })

        // Start new group
        currentGroup = [channel]
        currentStartNumber = channel.logicalChannel
        currentCategory = channel.category
        continue
      }
    }

    currentGroup.push(channel)
  }

  // Add final group
  if (currentGroup.length > 0) {
    const endNumber =
      currentGroup.length > 0 &&
      currentGroup[currentGroup.length - 1].logicalChannel != null
        ? currentGroup[currentGroup.length - 1].logicalChannel
        : null
    groups.push({
      id: generateId(),
      startNumber: currentStartNumber,
      endNumber,
      channels: currentGroup,
      category: currentCategory
    })
  }

  return groups
}

/**
 * Group channels by gaps in numbering (gap > 1 starts new group)
 * Fallback for lineups without category info
 */
function unflattenWithGapDetection(sorted: Channel[]): ChannelGroup[] {
  const groups: ChannelGroup[] = []
  let currentGroup: Channel[] = []
  let currentStartNumber: number | null = sorted[0]?.logicalChannel ?? null

  for (let i = 0; i < sorted.length; i++) {
    const channel = sorted[i]
    const prevChannel = sorted[i - 1]

    // Detect gap (difference > 1)
    if (
      prevChannel &&
      channel.logicalChannel != null &&
      prevChannel.logicalChannel != null
    ) {
      const gap = channel.logicalChannel - prevChannel.logicalChannel

      if (gap > 1) {
        // Gap detected - finalize current group
        const endNumber =
          currentGroup.length > 0 &&
          currentGroup[currentGroup.length - 1].logicalChannel != null
            ? currentGroup[currentGroup.length - 1].logicalChannel
            : null
        groups.push({
          id: generateId(),
          startNumber: currentStartNumber,
          endNumber,
          channels: currentGroup
        })

        // Start new group at this channel
        currentGroup = [channel]
        currentStartNumber = channel.logicalChannel
        continue
      }
    }

    currentGroup.push(channel)
  }

  // Add final group
  if (currentGroup.length > 0) {
    const endNumber =
      currentGroup.length > 0 &&
      currentGroup[currentGroup.length - 1].logicalChannel != null
        ? currentGroup[currentGroup.length - 1].logicalChannel
        : null
    groups.push({
      id: generateId(),
      startNumber: currentStartNumber,
      endNumber,
      channels: currentGroup
    })
  }

  return groups
}

/**
 * Renumber channels in a group sequentially from startNumber
 */
export function renumberChannelsInGroup(
  channels: Channel[],
  startNumber: number
): Channel[] {
  return channels.map((channel, index) => ({
    ...channel,
    logicalChannel: startNumber + index
  }))
}

/**
 * Smart renumber channels in a group, respecting drop position and filling gaps
 * @param channels - Channels in the group (already includes newly added channel at correct index)
 * @param startNumber - Group start number
 * @param insertIndex - Where the new channel was inserted
 * @returns Object with renumbered channels and new endNumber
 */
export function smartRenumberGroup(
  channels: Channel[],
  startNumber: number,
  insertIndex: number
): { channels: Channel[]; endNumber: number } {
  if (channels.length === 0) {
    return { channels: [], endNumber: startNumber }
  }

  const updatedChannels = [...channels]

  // Determine target logical number for inserted channel
  let targetNumber: number
  if (insertIndex === 0) {
    targetNumber = startNumber
  } else {
    const prevChannel = channels[insertIndex - 1]
    if (
      typeof prevChannel.logicalChannel === 'number' &&
      Number.isFinite(prevChannel.logicalChannel)
    ) {
      targetNumber = prevChannel.logicalChannel + 1
    } else {
      targetNumber = startNumber + insertIndex
    }
  }

  // Assign the target number to the inserted channel
  updatedChannels[insertIndex] = {
    ...updatedChannels[insertIndex],
    logicalChannel: targetNumber
  }

  // Shift all subsequent channels by incrementing their numbers sequentially
  for (let i = insertIndex + 1; i < updatedChannels.length; i++) {
    const prevChannelNumber = updatedChannels[i - 1].logicalChannel
    updatedChannels[i] = {
      ...updatedChannels[i],
      logicalChannel: prevChannelNumber + 1
    }
  }

  // Calculate new endNumber
  const endNumber = updatedChannels[updatedChannels.length - 1].logicalChannel

  return { channels: updatedChannels, endNumber }
}

/**
 * Renumber channels preserving gaps - only resolve conflicts
 * Conflicts occur when channel numbers are duplicates or out of order
 * Stops renumbering when a gap is encountered
 *
 * @param channels - Channels to renumber (assumed to be in display order)
 * @param fromIndex - Start renumbering from this index (default 0)
 * @returns Renumbered channels with gaps preserved
 */
export function renumberChannelsGapAware(
  channels: Channel[],
  fromIndex: number = 0
): Channel[] {
  if (channels.length === 0 || fromIndex >= channels.length) {
    return channels
  }

  const result = [...channels]

  // Start from the first channel we're renumbering
  for (let i = fromIndex; i < result.length; i++) {
    const currentChannel = result[i]
    const prevChannel = i > 0 ? result[i - 1] : null

    if (!prevChannel) {
      // First channel, keep its number
      continue
    }

    // Check for conflict: current number <= previous number
    if (currentChannel.logicalChannel <= prevChannel.logicalChannel) {
      // Conflict! Assign next sequential number
      result[i] = {
        ...currentChannel,
        logicalChannel: prevChannel.logicalChannel + 1
      }
    } else if (currentChannel.logicalChannel > prevChannel.logicalChannel + 1) {
      // Gap detected! Stop renumbering to preserve it
      break
    }
    // else: No conflict, no gap - continue checking
  }

  return result
}

/**
 * Detect overlaps between groups
 * An overlap occurs when a group's end channel number >= next group's start number
 */
export function detectGroupOverlaps(groups: ChannelGroup[]): GroupOverlap[] {
  const overlaps: GroupOverlap[] = []

  for (let i = 0; i < groups.length - 1; i++) {
    const currentGroup = groups[i]
    const nextGroup = groups[i + 1]

    // Skip if next group has no explicit start (ungrouped continuation)
    if (nextGroup.startNumber === null) continue
    if (currentGroup.startNumber === null) continue

    const currentGroupEnd =
      currentGroup.startNumber + currentGroup.channels.length - 1
    const nextGroupStart = nextGroup.startNumber

    if (currentGroupEnd >= nextGroupStart) {
      overlaps.push({
        groupId: currentGroup.id,
        nextGroupId: nextGroup.id,
        overlapCount: currentGroupEnd - nextGroupStart + 1,
        currentGroupEnd,
        nextGroupStart,
        suggestions: generateOverlapSuggestions(
          currentGroup,
          nextGroup,
          currentGroupEnd
        )
      })
    }
  }

  return overlaps
}

/**
 * Generate suggestions for resolving a group overlap
 */
function generateOverlapSuggestions(
  currentGroup: ChannelGroup,
  nextGroup: ChannelGroup,
  currentGroupEnd: number
): OverlapSuggestion[] {
  const suggestions: OverlapSuggestion[] = []
  const nextGroupStart = nextGroup.startNumber!

  // Suggestion 1: Advance next group to next tens boundary
  const nextTens = Math.ceil((currentGroupEnd + 1) / 10) * 10
  suggestions.push({
    type: 'advance_tens',
    description: `Move second group to channel ${nextTens}`,
    subDescription: 'next tens boundary, most practical',
    action: {
      groupId: nextGroup.id,
      newStartNumber: nextTens
    }
  })

  // Suggestion 2: Advance next group by +1 (just past overlap)
  const justPast = currentGroupEnd + 1
  if (justPast !== nextTens) {
    suggestions.push({
      type: 'advance_one',
      description: `Move second group to channel ${justPast}`,
      subDescription: 'after first group',
      action: {
        groupId: nextGroup.id,
        newStartNumber: justPast
      }
    })
  }

  // Suggestion 3: Change current group start
  if (currentGroup.startNumber !== null) {
    const newCurrentStart = nextGroupStart - currentGroup.channels.length
    if (newCurrentStart > 0) {
      suggestions.push({
        type: 'change_current',
        description: `Move first group to channel ${newCurrentStart}`,
        subDescription: 'might overlap with another group',
        action: {
          groupId: currentGroup.id,
          newStartNumber: newCurrentStart
        }
      })
    }
  }

  // Suggestion 4: Remove channels from current group
  const channelsToRemove = currentGroupEnd - nextGroupStart + 1
  suggestions.push({
    type: 'remove_channels',
    description: `Remove ${channelsToRemove} channel(s) from first group`,
    subDescription: 'this will delete channels from the end of the group',
    action: {
      groupId: currentGroup.id,
      channelsToRemove
    }
  })

  // Suggestion 5: Consume overlap (NEW)
  // Move overlapping channels from current group to next group
  const overlapStart = nextGroupStart
  const overlapEnd = currentGroupEnd
  const channelsToTransfer = currentGroup.channels.filter(
    (ch) =>
      ch.logicalChannel != null &&
      ch.logicalChannel >= overlapStart &&
      ch.logicalChannel <= overlapEnd
  )

  if (channelsToTransfer.length > 0) {
    suggestions.push({
      type: 'consume_overlap',
      description: `Move ${channelsToTransfer.length} overlapping channel(s) to second group`,
      subDescription: `channels ${overlapStart}-${overlapEnd} will move to second group`,
      action: {
        groupId: currentGroup.id,
        newEndNumber: overlapStart - 1, // Current group ends just before overlap
        channelsToTransfer: channelsToTransfer.map((ch) => ch.id)
      }
    })
  }

  return suggestions
}

/**
 * Detect channels that exceed their group's defined range
 */
export function detectOutOfRangeChannels(
  groups: ChannelGroup[]
): ValidationError[] {
  const errors: ValidationError[] = []

  for (const group of groups) {
    if (group.startNumber === null || group.endNumber === null) continue

    const outOfRangeChannels = group.channels.filter(
      (ch) =>
        ch.logicalChannel != null &&
        (ch.logicalChannel < group.startNumber! ||
          ch.logicalChannel > group.endNumber!)
    )

    if (outOfRangeChannels.length > 0) {
      // Calculate suggested fixes
      const validChannels = group.channels
        .filter((ch) => ch.logicalChannel != null)
        .map((ch) => ch.logicalChannel!)
      const maxChannel = Math.max(...validChannels)
      const minChannel = Math.min(...validChannels)
      const suggestedFixes: import('./guideTypes').OutOfRangeFix[] = []

      // Check if channels exceed upper bound
      const hasUpperBoundViolation = maxChannel > group.endNumber
      const hasLowerBoundViolation = minChannel < group.startNumber

      if (hasLowerBoundViolation) {
        // Fix 1: Reduce start to previous tens boundary
        const prevTens = Math.floor(minChannel / 10) * 10
        suggestedFixes.push({
          type: 'reduce_start_to_tens',
          description: `Reduce group start to channel ${prevTens}`,
          newStartNumber: prevTens
        })

        // Fix 2: Reduce start by exact number of channels out of range
        suggestedFixes.push({
          type: 'reduce_start_by_count',
          description: `Reduce group start to channel ${minChannel}`,
          newStartNumber: minChannel
        })
      }

      if (hasUpperBoundViolation) {
        // Fix 1: Extend to next tens boundary
        const nextTens = Math.ceil(maxChannel / 10) * 10
        suggestedFixes.push({
          type: 'extend_to_tens',
          description: `Extend group end to channel ${nextTens}`,
          newEndNumber: nextTens
        })

        // Fix 2: Extend by exact number of channels out of range
        suggestedFixes.push({
          type: 'extend_by_count',
          description: `Extend group end to channel ${maxChannel}`,
          newEndNumber: maxChannel
        })
      }

      // Construct appropriate error message
      let message = ''
      if (hasLowerBoundViolation && hasUpperBoundViolation) {
        message = `Group ${group.startNumber}-${group.endNumber} has ${outOfRangeChannels.length} channel(s) outside range (both below and above)`
      } else if (hasLowerBoundViolation) {
        message = `Group ${group.startNumber}-${group.endNumber} has ${outOfRangeChannels.length} channel(s) below start range`
      } else {
        message = `Group ${group.startNumber}-${group.endNumber} has ${outOfRangeChannels.length} channel(s) above end range`
      }

      errors.push({
        type: 'out_of_range',
        message,
        channelIds: outOfRangeChannels.map((ch) => ch.id),
        groupIds: [group.id],
        suggestedFixes
      })
    }
  }

  return errors
}

/**
 * Validate lineup for save
 */
export function validateLineup(groups: ChannelGroup[]): ValidationResult {
  const errors: ValidationError[] = []
  const seenNumbers = new Map<number, string[]>() // number -> channelIds

  // Check 1: Must have at least one channel
  if (groups.length === 0 || groups.every((g) => g.channels.length === 0)) {
    errors.push({
      type: 'missing_name',
      message: 'Lineup must contain at least one channel'
    })
  }

  // Check 2: Multiple start channels
  const startChannels = groups.flatMap((g) =>
    g.channels.filter((ch) => ch.startChannel).map((ch) => ch.id)
  )
  if (startChannels.length > 1) {
    errors.push({
      type: 'multiple_start_channels',
      message: `Multiple start channels detected (only one allowed)`,
      channelIds: startChannels
    })
  }

  for (const group of groups) {
    // Check 3: Verify channels fall within group range (allows gaps)
    if (group.startNumber !== null && group.endNumber !== null) {
      for (const channel of group.channels) {
        if (
          channel.logicalChannel < group.startNumber ||
          channel.logicalChannel > group.endNumber
        ) {
          errors.push({
            type: 'invalid_sequence',
            message: `Channel ${channel.logicalChannel} is outside group range (${group.startNumber}-${group.endNumber})`,
            channelIds: [channel.id],
            groupIds: [group.id]
          })
        }
      }
    }

    // Check 3a: Verify channels are in sequential order (detect out-of-order channels)
    // Example: (40,41,42,46,44,45) should error because 46 > 44 but comes before it
    for (let i = 0; i < group.channels.length - 1; i++) {
      const current = group.channels[i]
      const next = group.channels[i + 1]
      if (current.logicalChannel >= next.logicalChannel) {
        errors.push({
          type: 'invalid_sequence',
          message: `Channels are out of order: channel ${current.logicalChannel} comes before channel ${next.logicalChannel}`,
          channelIds: [current.id, next.id],
          groupIds: [group.id]
        })
      }
    }

    for (const channel of group.channels) {
      // Check 4: Required displayName
      if (!channel.displayName?.trim()) {
        errors.push({
          type: 'missing_name',
          message: `Channel ${channel.logicalChannel} is missing a display name`,
          channelIds: [channel.id]
        })
      }

      // Track channel numbers for duplicate detection
      if (channel.logicalChannel != null) {
        if (!seenNumbers.has(channel.logicalChannel)) {
          seenNumbers.set(channel.logicalChannel, [])
        }
        seenNumbers.get(channel.logicalChannel)!.push(channel.id)
      }
    }
  }

  // Check 5: Unique logicalChannel across all groups
  seenNumbers.forEach((channelIds, number) => {
    if (channelIds.length > 1) {
      errors.push({
        type: 'duplicate',
        message: `Duplicate channel number ${number}`,
        channelIds
      })
    }
  })

  // Check 6: Detect out-of-range channels
  const outOfRangeErrors = detectOutOfRangeChannels(groups)
  errors.push(...outOfRangeErrors)

  // Check 7: Detect overlaps
  const overlaps = detectGroupOverlaps(groups)
  if (overlaps.length > 0) {
    for (const overlap of overlaps) {
      errors.push({
        type: 'overlap',
        message: `Group overlap detected: group ending at ${overlap.currentGroupEnd} overlaps with group starting at ${overlap.nextGroupStart}`,
        groupIds: [overlap.groupId, overlap.nextGroupId]
      })
    }
  }

  return {
    valid: errors.length === 0 && overlaps.length === 0,
    errors,
    overlaps
  }
}

/**
 * Validate imported lineup for duplicates and invalid sequences
 * Used during import preview to reject bad data before importing
 */
export function validateImportedLineup(groups: ChannelGroup[]): {
  valid: boolean
  errors: ValidationError[]
} {
  const errors: ValidationError[] = []
  const seenNumbers = new Map<number, string[]>()

  for (const group of groups) {
    // Check that channels fall within group range (allows gaps)
    if (group.startNumber !== null && group.endNumber !== null) {
      for (const channel of group.channels) {
        if (
          channel.logicalChannel < group.startNumber ||
          channel.logicalChannel > group.endNumber
        ) {
          errors.push({
            type: 'invalid_sequence',
            message: `Channel ${channel.logicalChannel} is outside group range (${group.startNumber}-${group.endNumber}) in imported data`,
            channelIds: [channel.id],
            groupIds: [group.id]
          })
        }
      }
    }

    // Track channel numbers for duplicate detection
    for (const channel of group.channels) {
      if (channel.logicalChannel != null) {
        if (!seenNumbers.has(channel.logicalChannel)) {
          seenNumbers.set(channel.logicalChannel, [])
        }
        seenNumbers.get(channel.logicalChannel)!.push(channel.id)
      }
    }
  }

  // Check for duplicates
  seenNumbers.forEach((channelIds, number) => {
    if (channelIds.length > 1) {
      errors.push({
        type: 'duplicate',
        message: `Duplicate channel number ${number} in imported data`,
        channelIds
      })
    }
  })

  return {
    valid: errors.length === 0,
    errors
  }
}

/**
 * Convert frontend groups to backend ChannelLineupType
 */
export function groupsToChannelLineup(
  groups: ChannelGroup[],
  comment?: string,
  createdBy?: string,
  lastModifiedBy?: string
): ChannelLineupType {
  const flatChannels = flattenGroupsToLineup(groups)

  // Remove frontend-only fields (id is frontend-only)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const lineup = flatChannels.map(({ id, ...channel }) => channel)

  // Serialize group metadata (explicit start/end numbers and categories)
  const groupsMetadata = groups
    .filter((g) => g.startNumber !== null && g.endNumber !== null)
    .map((g) => ({
      startNumber: g.startNumber!,
      endNumber: g.endNumber!,
      category: g.category
    }))

  return {
    lineup,
    metadata: {
      comment,
      createdBy,
      lastModifiedBy,
      groups: groupsMetadata.length > 0 ? groupsMetadata : undefined
    }
  }
}

/**
 * Convert backend lineup with metadata to frontend groups
 * Uses explicit group definitions from metadata.groups if available,
 * otherwise falls back to gap-based detection
 */
export function channelLineupToGroupsWithMetadata(
  lineup: ChannelLineupType['lineup'],
  metadata?: ChannelLineupType['metadata']
): ChannelGroup[] {
  // Add frontend IDs to channels and ensure logicalChannel is a valid number
  const channelsWithIds = lineup.map((channel) => {
    let logicalChannel = channel.logicalChannel

    // Parse logicalChannel as number in case backend sends strings
    if (typeof channel.logicalChannel === 'string') {
      const parsed = parseInt(channel.logicalChannel, 10)
      logicalChannel = Number.isNaN(parsed) ? 0 : parsed
    }

    return {
      ...channel,
      id: generateId(),
      logicalChannel
    }
  })

  // If metadata.groups exists, use explicit group definitions
  if (metadata?.groups && metadata.groups.length > 0) {
    const groups: ChannelGroup[] = []

    for (const groupDef of metadata.groups) {
      // Extract channels that fall within this group's range
      const groupChannels = channelsWithIds.filter(
        (ch) =>
          ch.logicalChannel >= groupDef.startNumber &&
          ch.logicalChannel <= groupDef.endNumber
      )

      if (groupChannels.length > 0) {
        // Sort channels by logical channel number
        groupChannels.sort((a, b) => a.logicalChannel - b.logicalChannel)

        groups.push({
          id: generateId(),
          startNumber: groupDef.startNumber,
          endNumber: groupDef.endNumber,
          channels: groupChannels,
          category: groupDef.category
        })
      }
    }

    return groups
  }

  // Fallback to gap-based detection for old lineups
  return unflattenLineupToGroups(channelsWithIds)
}

/**
 * Export lineup to JSON (flat array only, exactly as stored on hub)
 */
export function exportLineupToJSON(groups: ChannelGroup[]): string {
  const flatChannels = flattenGroupsToLineup(groups)

  // Remove frontend-only fields (id is frontend-only)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const channels = flatChannels.map(({ id, ...channel }) => channel)

  // Export as raw array, exactly like hub storage format
  return JSON.stringify(channels, null, 2)
}

/**
 * Export lineup to CSV (flat list only, no groups)
 */
export function exportLineupToCSV(groups: ChannelGroup[]): string {
  const flatChannels = flattenGroupsToLineup(groups)

  // CSV header - matches all fields in JSON format
  const header =
    'Logical Channel,Display Name,Channel Name,Channel ID,Icon,IP Address,IP Port,Major,Minor,Tune Type,Is Brand Video,Title,Description,Category,Start Channel'

  // CSV rows
  const rows = flatChannels.map((channel) => {
    return [
      channel.logicalChannel ?? '',
      channel.displayName ?? '',
      channel.channelName ?? '',
      channel.channelId ?? '',
      channel.channelIcon ?? '',
      channel.ipAddress ?? '',
      channel.ipPort ?? '',
      channel.major ?? '',
      channel.minor ?? '',
      channel.tuneType ?? '',
      channel.isBrandVideo ?? false,
      channel.title ?? '',
      channel.description ?? '',
      channel.category ?? '',
      channel.startChannel ?? false
    ]
      .map((val) => {
        // Escape commas and quotes in CSV
        const str = String(val)
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`
        }
        return str
      })
      .join(',')
  })

  return [header, ...rows].join('\n')
}

/**
 * Parse imported JSON lineup (expects raw array format like hub storage)
 */
export function parseImportedJSON(json: string): {
  groups: ChannelGroup[]
  lineupName: string
  comment?: string
} | null {
  try {
    const data = JSON.parse(json)

    // Accept raw array format (hub storage format)
    if (!Array.isArray(data)) {
      return null
    }

    // Add frontend IDs and ensure logicalChannel is a valid number
    const channelsWithIds = data.map((channel: Channel) => {
      let logicalChannel = channel.logicalChannel

      // Parse logicalChannel as number in case JSON contains strings
      if (typeof channel.logicalChannel === 'string') {
        const parsed = parseInt(channel.logicalChannel, 10)
        logicalChannel = Number.isNaN(parsed) ? 0 : parsed
      }

      return {
        ...channel,
        id: generateId(),
        logicalChannel
      }
    })

    // Derive groups from gaps
    const groups = unflattenLineupToGroups(channelsWithIds)

    return {
      groups,
      lineupName: 'Imported Lineup',
      comment: undefined
    }
  } catch (error) {
    console.error('Failed to parse JSON:', error)
    return null
  }
}

/**
 * Parse imported CSV lineup
 */
export function parseImportedCSV(
  csv: string
): { groups: ChannelGroup[] } | null {
  try {
    const lines = csv.trim().split('\n')
    if (lines.length < 2) return null

    // Skip header row (first line)
    const rows = lines.slice(1)

    // Simple CSV parsing (handles basic cases)
    const channels: Channel[] = []

    for (const row of rows) {
      const values = row
        .split(',')
        .map((val) => val.trim().replace(/^"|"$/g, ''))

      // Map columns (order: Logical Channel,Display Name,Channel Name,Channel ID,Icon,IP Address,IP Port,Major,Minor,Tune Type,Is Brand Video,Title,Description,Category,Start Channel)
      const tuneTypeValue = values[9] || null
      const channel: Channel = {
        id: generateId(),
        logicalChannel: values[0] ? parseInt(values[0], 10) : 0,
        displayName: values[1] || '',
        channelName: values[2] || '',
        channelId: values[3] || '',
        channelIcon: values[4] || '',
        ipAddress: values[5] || '',
        ipPort: values[6] || '',
        major: values[7] || '',
        minor: values[8] || '',
        tuneType:
          tuneTypeValue === 'IP' ||
          tuneTypeValue === 'RF' ||
          tuneTypeValue === 'LG'
            ? tuneTypeValue
            : null,
        isBrandVideo: values[10] === 'true' || values[10] === '1',
        title: values[11] || '',
        description: values[12] || '',
        category: values[13] || undefined,
        startChannel: values[14] === 'true' || values[14] === '1'
      }

      if (channel.logicalChannel != null) {
        channels.push(channel)
      }
    }

    // Derive groups from gaps
    const groups = unflattenLineupToGroups(channels)

    return { groups }
  } catch (error) {
    console.error('Failed to parse CSV:', error)
    return null
  }
}

/**
 * Get all channels from groups sorted by logicalChannel
 */
export function getChannelsInLogicalOrder(groups: ChannelGroup[]): Channel[] {
  const allChannels = groups.flatMap((g) => g.channels)
  return allChannels.sort((a, b) => a.logicalChannel - b.logicalChannel)
}

/**
 * Auto-populate group categories when all channels share the same category
 * Only sets group category if it's currently undefined/empty
 */
export function autoPopulateGroupCategories(
  groups: ChannelGroup[]
): ChannelGroup[] {
  return groups.map((group) => {
    // Skip if group already has a category
    if (group.category) {
      return group
    }

    // Skip if group has no channels
    if (group.channels.length === 0) {
      return group
    }

    // Get all unique categories in the group (filter out empty/undefined)
    const categories = group.channels
      .map((ch) => ch.category)
      .filter((cat): cat is string => !!cat && cat.trim() !== '')

    // If all channels have the same category, set it as group category
    if (categories.length > 0) {
      const uniqueCategories = [...new Set(categories)]
      if (uniqueCategories.length === 1) {
        return {
          ...group,
          category: uniqueCategories[0]
        }
      }
    }

    return group
  })
}

/**
 * Format channel tune info for display
 * Returns formatted string like "192.168.1.1:8080" or "3-1"
 */
export function getChannelTuneInfo(channel: Channel): string {
  if (channel.tuneType === 'IP' && channel.ipAddress) {
    const port = channel.ipPort ? `:${channel.ipPort}` : ''
    return `${channel.ipAddress}${port}`
  }

  if (
    (channel.tuneType === 'LG' || channel.tuneType === 'RF') &&
    channel.major &&
    channel.minor
  ) {
    return `${channel.major}-${channel.minor}`
  }

  return ''
}
